import { resolveCoords } from "./airports";
import { coerceClockDate, formatHmInZone, formatUtcHm } from "./clock";
import { ganttDisplayTimeZone, hydrateGanttTimeDisplay } from "./ganttBoard";
import { formatDiversionLine } from "./diversion";
import {
  resolveCurrentFlight,
  type CurrentFlightReason,
  type FlightBindingAuthority,
} from "./flightBinding";
import { displayFlightNumber } from "./flightNumber";
import { applyApproachOverride, deriveFlightPhase, sampleSpeedKt, type FlightPhaseView } from "./flightPhase";
import {
  deriveFlightOpsStatus,
  FLIGHT_OPS_STATUS_LABEL,
  ticketStaMs,
  ticketStdMs,
  type FlightOpsStatus,
} from "./flightOpsStatus";
import { groundTicket, pickCurrentFlightTicket, type GroundingVerdict, type TicketGrounding } from "./flightGrounding";
import { pickCurrentTicket, ticketLifecycle, TICKET_LIFECYCLE_LABEL } from "./opsBoard";
import { haversineNm } from "./geo";
import { isFreshSample, type FusedPosition } from "./position";
import { ticketStatusPillClass } from "./statusColor";
import { parseAtdIso, resolveTicketAtd, type SimAtdTimes } from "./ticketAtd";
import type {
  AircraftSpec,
  CrewMember,
  GanttTimeDisplay,
  Mission,
  PilotProfile,
  SceneryAirport,
  TicketLifecycle,
} from "./types";

/**
 * One ops-strip row. Solo desks emit length 1. Extra pilots with an
 * assignment or published fix append more rows — same `FlightPhaseBar`.
 */
export type OpsStripRow = {
  id: string;
  pilotName: string;
  crewId: string;
  aircraftType: string;
  registration: string;
  /** OFP / SI radio callsign when known. Map label prefers this over tail. */
  callsign: string;
  /**
   * Ticket flight# (CEL#### / F=ferry). Optional so companion / older row
   * builders stay valid. Empty when no ticket — never invented.
   */
  flightNumber?: string;
  missionId?: string;
  lifecycle?: TicketLifecycle | null;
  lifecycleLabel?: string;
  lifecyclePillClass?: string;
  /** `KBEC→CYPD` when an assigned / active trip exists. */
  route: string;
  phase: FlightPhaseView;
  etaLabel: string;
  etaAtUtc: string | null;
  remainingNm: number | null;
  /** Planned / great-circle length for the active trip. Null when no route. */
  totalNm: number | null;
  etaSource: OpsEtaSource;
  eteMin: number | null;
  eteLabel: string;
  /** Locked ticket STA, painted Local or Z from Settings OCC / Gantt clock. Empty when STD/ETE is missing. */
  staLabel?: string;
  staAtUtc?: string | null;
  /** Live/plan ETA vs locked STA. Null when either clock is missing. */
  etaVsSta?: OpsEtaVsSta;
  etaVsStaMin?: number | null;
  etaVsStaLabel?: string;
  divertToIcao?: string;
  divertReason?: string;
  divertLine?: string;
  /** Why this ticket is current — Mark Active sticks. */
  bindReason?: CurrentFlightReason | null;
  bindLabel?: string;
  /**
   * Live flight clock. Optional so companion / older row builders stay valid.
   * Empty (`—`) when ATD / wheels-off / out is unknown — never invented.
   */
  elapsedMin?: number | null;
  elapsedLabel?: string;
  remainingMin?: number | null;
  remainingLabel?: string;
  flightTimeSource?: OpsFlightTimeSource;
  /** `GS` · `OFP` · `plan` — empty when remaining is unknown. */
  flightTimeSourceLabel?: string;
  /** Where elapsed started. Null when elapsed is empty. */
  elapsedFrom?: OpsElapsedFrom | null;
  /** Live kinematics from the fused sample (SimConnect preferred). Null → `—`. */
  altitudeFt: number | null;
  groundspeedKt: number | null;
  headingDeg: number | null;
  headingKind: "track" | "heading" | null;
  squawkCode: string;
  altitudeLabel: string;
  groundspeedLabel: string;
  headingLabel: string;
  squawkLabel: string;
  /** Indicated airspeed / vertical speed. Optional so surfaces that build their own rows can adopt them later. */
  airspeedKt?: number | null;
  verticalSpeedFpm?: number | null;
  airspeedLabel?: string;
  verticalSpeedLabel?: string;
  trueAirspeedKt?: number | null;
  outsideAirTempC?: number | null;
  trueAirspeedLabel?: string;
  outsideAirTempLabel?: string;
  /** Schedule ops status when a filed trip is on the strip. Null with no ticket. */
  opsStatus: FlightOpsStatus | null;
  opsStatusLabel: string;
  /**
   * Does the sim back the ticket on this row? The strip pairs a scheduled
   * ticket with a live phase, so it has to say when those disagree instead of
   * implying the pilot is flying the next STD. Optional so surfaces that build
   * their own rows (companion) can adopt it on their own release.
   */
  grounding?: GroundingVerdict | null;
  groundingNote?: string;
  /** VATSIM network-online hint. Empty when ops assist is off or unmatched. */
  networkNote?: string;
  /** Live trip-vs-actual fuel. Empty when the leg is not being measured. */
  fuelLabel?: string;
  fuelTitle?: string;
};

export type OpsEtaSource = "live" | "plan" | "none";
export type OpsFlightTimeSource = "live" | "plan" | "none";
export type OpsElapsedFrom = "atd" | "wheels-off" | "out";
export type OpsEtaVsSta = "early" | "late" | "on" | null;

export type OpsStripPilotInput = {
  crew?: CrewMember | null;
  profile?: PilotProfile | null;
  mission?: Mission | null;
  fused?: FusedPosition | null;
  callsign?: string | null;
  /** Live SimConnect phase is only honest for the local desk. */
  phase?: FlightPhaseView;
  simconnectEnabled?: boolean;
  simconnectError?: string;
  /** Live wheels-off / out when the ticket ATD is still empty. */
  telemetry?: SimAtdTimes | null;
  /** VATSIM lock line for the tracker / strip. Never invented ATC. */
  networkNote?: string | null;
  /** Desk / Megan Mark Active id — wins over sim / schedule. */
  activeFlightId?: string | null;
  activeFlightAuthority?: FlightBindingAuthority | null;
  bindReason?: CurrentFlightReason | null;
  /** Live fuel readout for this desk only. Not copied onto other pilots. */
  fuelLabel?: string | null;
  fuelTitle?: string | null;
};

export type BuildOpsStripInput = OpsStripPilotInput & {
  missions?: Mission[];
  fleet?: AircraftSpec[];
  scenery?: SceneryAirport[];
  now?: Date;
  /**
   * Other company pilots (assignment and/or published presence).
   * A row is emitted only when a mission or a usable fused fix exists —
   * never a fake lat/lon. Extra-pilot kinematics require that published
   * fused fix; we do not invent a fused multi-pilot track.
   */
  extraPilots?: OpsStripPilotInput[];
  /**
   * Settings OCC / Schedule Local/Z. Instant fields stay UTC (`staAtUtc` /
   * `etaAtUtc`); labels paint Local at the ops zone or Z. Omit to keep
   * Zulu labels for companion / older builders.
   */
  timeDisplay?: GanttTimeDisplay;
  /** IANA zone for Local labels. Ignored when timeDisplay is zulu. */
  timeZone?: string;
};

export type OpsKinematics = {
  altitudeFt: number | null;
  groundspeedKt: number | null;
  headingDeg: number | null;
  headingKind: "track" | "heading" | null;
  squawkCode: string;
  airspeedKt: number | null;
  verticalSpeedFpm: number | null;
  altitudeLabel: string;
  groundspeedLabel: string;
  headingLabel: string;
  squawkLabel: string;
  airspeedLabel: string;
  verticalSpeedLabel: string;
  trueAirspeedKt: number | null;
  outsideAirTempC: number | null;
  trueAirspeedLabel: string;
  outsideAirTempLabel: string;
};

/** GS / IAS must be at least this to estimate arrival from remaining NM. */
export const MIN_LIVE_ETA_KT = 40;

const BLANK = "—";

function trim(value?: string | null): string {
  return typeof value === "string" ? value.trim() : "";
}

function finiteOrNull(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** Rounded feet, or `—`. Never invents a value. */
export function formatOpsAltitude(value: number | null | undefined): string {
  const n = finiteOrNull(value);
  return n == null ? BLANK : String(Math.round(n));
}

/** Rounded knots, or `—`. */
export function formatOpsGroundspeed(value: number | null | undefined): string {
  const n = finiteOrNull(value);
  return n == null ? BLANK : String(Math.round(Math.abs(n)));
}

/** Signed fpm to the nearest 10 (`+1500`, `-800`, `0`), or `—`. */
export function formatOpsVerticalSpeed(value: number | null | undefined): string {
  const n = finiteOrNull(value);
  if (n == null) return BLANK;
  const rounded = Math.round(n / 10) * 10;
  if (rounded === 0) return "0";
  return rounded > 0 ? `+${rounded}` : String(rounded);
}

/** Whole °C (`-56`), or `—`. */
export function formatOpsTemperature(value: number | null | undefined): string {
  const n = finiteOrNull(value);
  if (n == null) return BLANK;
  const rounded = Math.round(n);
  return String(rounded === 0 ? 0 : rounded);
}

/** 3-digit true heading (`360°` = north). Track wins when the sample has it. */
export function formatOpsHeading(value: number | null | undefined): string {
  const n = finiteOrNull(value);
  if (n == null) return BLANK;
  const deg = ((Math.round(n) % 360) + 360) % 360;
  return `${String(deg === 0 ? 360 : deg).padStart(3, "0")}°`;
}

function squawkDigitsOk(n: number): boolean {
  return /^[0-7]{1,4}$/.test(String(n));
}

/** Decimal 1200 or BCD16 `0x1200`. Invalid / missing → null. */
export function parseSquawkCode(value: number | null | undefined): string | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return null;
  const n = Math.round(value);
  if (squawkDigitsOk(n) && n <= 7777) return String(n).padStart(4, "0");
  const bcd =
    ((n >> 12) & 0xf) * 1000 + ((n >> 8) & 0xf) * 100 + ((n >> 4) & 0xf) * 10 + (n & 0xf);
  if (squawkDigitsOk(bcd) && bcd <= 7777) return String(bcd).padStart(4, "0");
  return null;
}

export function formatOpsSquawk(value: number | null | undefined): string {
  return parseSquawkCode(value) ?? BLANK;
}

/** Hours:minutes remaining (`1:12`). */
export function formatOpsEte(min: number | null | undefined): string {
  if (typeof min !== "number" || !Number.isFinite(min) || min < 0) return BLANK;
  const total = Math.round(min);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

/**
 * Kinematics from the fused sample. Fusion already prefers fresh SimConnect
 * over SI over last-known — we do not invent numbers or phase here.
 */
export function opsKinematicsFromFused(fused?: FusedPosition | null): OpsKinematics {
  const altitudeFt = finiteOrNull(fused?.altitudeFt);
  const groundspeedKt = finiteOrNull(fused?.groundSpeed) ?? finiteOrNull(fused?.groundSpeedKt);
  const trackDeg = finiteOrNull(fused?.trackDeg);
  const headingDeg = trackDeg ?? finiteOrNull(fused?.heading);
  const headingKind: OpsKinematics["headingKind"] = trackDeg != null ? "track" : headingDeg != null ? "heading" : null;
  const squawkCode = parseSquawkCode(fused?.squawk) ?? "";
  const airspeedKt = finiteOrNull(fused?.airspeed);
  const verticalSpeedFpm = finiteOrNull(fused?.verticalSpeedFpm);
  const trueAirspeedKt = finiteOrNull(fused?.trueAirspeedKt);
  const outsideAirTempC = finiteOrNull(fused?.outsideAirTempC);
  return {
    altitudeFt,
    groundspeedKt,
    headingDeg,
    headingKind,
    squawkCode,
    airspeedKt,
    verticalSpeedFpm,
    trueAirspeedKt,
    outsideAirTempC,
    altitudeLabel: formatOpsAltitude(altitudeFt),
    groundspeedLabel: formatOpsGroundspeed(groundspeedKt),
    headingLabel: formatOpsHeading(headingDeg),
    squawkLabel: squawkCode || BLANK,
    airspeedLabel: formatOpsGroundspeed(airspeedKt),
    verticalSpeedLabel: formatOpsVerticalSpeed(verticalSpeedFpm),
    trueAirspeedLabel: formatOpsGroundspeed(trueAirspeedKt),
    outsideAirTempLabel: formatOpsTemperature(outsideAirTempC),
  };
}

function icao(value?: string | null): string {
  return trim(value).toUpperCase();
}

/** Human Mark Active first, then in-progress / airborne, then next STD. */
export function pickActiveMission(
  missions?: Mission[] | null,
  opts?: {
    activeFlightId?: string | null;
    activeFlightAuthority?: FlightBindingAuthority | null;
    fused?: FusedPosition | null;
    scenery?: SceneryAirport[];
    now?: Date;
  },
): Mission | undefined {
  const list = missions ?? [];
  return (
    pickCurrentFlightTicket({
      missions: list,
      activeFlightId: opts?.activeFlightId,
      activeFlightAuthority: opts?.activeFlightAuthority,
      fused: opts?.fused,
      scenery: opts?.scenery,
      now: opts?.now,
    }) ?? pickCurrentTicket({ missions: list }) ?? undefined
  );
}

export function bindLabelFor(reason?: CurrentFlightReason | null): string {
  if (reason === "human") return "Mark Active";
  if (reason === "jury") return "Sim jury";
  if (reason === "last_confident") return "Last bind";
  if (reason === "schedule") return "Schedule";
  return "";
}

/** Compare a projected ETA to locked ticket STA. Either missing → empty, never invented. */
export function compareEtaToSta(
  etaAtUtc: string | null | undefined,
  staMs: number | null | undefined,
): { etaVsSta: OpsEtaVsSta; etaVsStaMin: number | null; etaVsStaLabel: string } {
  const eta = coerceClockDate(etaAtUtc)?.getTime() ?? null;
  const sta = typeof staMs === "number" && Number.isFinite(staMs) ? staMs : null;
  if (eta == null || sta == null) {
    return { etaVsSta: null, etaVsStaMin: null, etaVsStaLabel: "" };
  }
  const deltaMin = Math.round((eta - sta) / 60_000);
  if (deltaMin === 0) return { etaVsSta: "on", etaVsStaMin: 0, etaVsStaLabel: "on STA" };
  if (deltaMin < 0) {
    return {
      etaVsSta: "early",
      etaVsStaMin: Math.abs(deltaMin),
      etaVsStaLabel: `${formatOpsEte(Math.abs(deltaMin))} early`,
    };
  }
  return {
    etaVsSta: "late",
    etaVsStaMin: deltaMin,
    etaVsStaLabel: `${formatOpsEte(deltaMin)} late`,
  };
}

export function opsRouteLabel(
  mission: Pick<Mission, "originIcao" | "destIcao" | "legs"> | null | undefined,
): string {
  if (!mission) return "";
  if (mission.legs && mission.legs.length > 1) {
    const first = mission.legs[0];
    if (first) {
      const hops = [first.originIcao, ...mission.legs.map((l) => l.destIcao)].map(icao).filter(Boolean);
      if (hops.length >= 2) return hops.join("→");
    }
  }
  const origin = icao(mission.originIcao);
  const dest = icao(mission.destIcao);
  if (!origin && !dest) return "";
  return `${origin}→${dest}`;
}

function planDeparture(mission: Mission): Date | null {
  const locked = ticketStdMs(mission);
  if (locked != null) return new Date(locked);
  return null;
}

/** Planned distance, else origin→dest great-circle. Used by the shared phase rail. */
export function routeTotalNm(
  mission?: Pick<Mission, "originIcao" | "destIcao" | "distanceNm"> | null,
  scenery: SceneryAirport[] = [],
): number | null {
  if (!mission) return null;
  const planned = finiteOrNull(mission.distanceNm);
  if (planned != null && planned >= 2) return planned;
  const origin = resolveCoords(mission.originIcao, scenery);
  const dest = resolveCoords(mission.destIcao, scenery);
  if (!origin || !dest) return null;
  const nm = haversineNm(origin.lat, origin.lon, dest.lat, dest.lon);
  return Number.isFinite(nm) && nm >= 2 ? nm : null;
}

/** Fresh SimConnect, else fresh SI, else fresh VATSIM session. Last-known never drives a live ETA. */
export function liveFixForEta(fused?: FusedPosition | null, now: Date = new Date()): FusedPosition | null {
  if (!fused || fused.stale) return null;
  if (fused.source !== "simconnect" && fused.source !== "sayintentions" && fused.source !== "vatsim") return null;
  if (!isFreshSample(fused, now)) return null;
  return fused;
}

export type EtaEstimate = {
  atUtc: string | null;
  label: string;
  remainingNm: number | null;
  source: OpsEtaSource;
  eteMin: number | null;
  eteLabel: string;
};

export function estimateArrival(input: {
  mission?: Mission | null;
  fused?: FusedPosition | null;
  scenery?: SceneryAirport[];
  now?: Date;
}): EtaEstimate {
  const now = input.now ?? new Date();
  const mission = input.mission;
  if (!mission) {
    return { atUtc: null, label: BLANK, remainingNm: null, source: "none", eteMin: null, eteLabel: BLANK };
  }

  const dest = resolveCoords(mission.destIcao, input.scenery ?? []);
  const fix = liveFixForEta(input.fused, now);
  let remainingNm: number | null = null;
  if (fix && dest) {
    remainingNm = haversineNm(fix.lat, fix.lon, dest.lat, dest.lon);
    const speed = sampleSpeedKt(fix);
    if (speed != null && speed >= MIN_LIVE_ETA_KT && remainingNm >= 2) {
      const eteMin = (remainingNm / speed) * 60;
      const eta = new Date(now.getTime() + eteMin * 60_000);
      return {
        atUtc: eta.toISOString(),
        label: formatUtcHm(eta),
        remainingNm,
        source: "live",
        eteMin,
        eteLabel: formatOpsEte(eteMin),
      };
    }
  }

  const dep = planDeparture(mission);
  const ete = typeof mission.eteMin === "number" && Number.isFinite(mission.eteMin) && mission.eteMin > 0
    ? mission.eteMin
    : null;
  if (dep && ete != null) {
    const eta = new Date(dep.getTime() + ete * 60_000);
    const remainingPlan = (eta.getTime() - now.getTime()) / 60_000;
    const eteMin = remainingPlan >= 0 ? remainingPlan : null;
    return {
      atUtc: eta.toISOString(),
      label: formatUtcHm(eta),
      remainingNm,
      source: "plan",
      eteMin,
      eteLabel: formatOpsEte(eteMin),
    };
  }

  return { atUtc: null, label: BLANK, remainingNm, source: "none", eteMin: null, eteLabel: BLANK };
}

export type FlightTimeEstimate = {
  elapsedMin: number | null;
  remainingMin: number | null;
  elapsedLabel: string;
  remainingLabel: string;
  source: OpsFlightTimeSource;
  sourceLabel: string;
  elapsedFrom: OpsElapsedFrom | null;
};

function minutesBetween(start: Date, end: Date): number {
  return (end.getTime() - start.getTime()) / 60_000;
}

/** Filed SimBrief ETE first, else ticket ETE / planned block. */
export function plannedFlightMin(mission?: Pick<Mission, "eteMin" | "ofpAttachment"> | null): {
  min: number;
  label: "OFP" | "plan";
} | null {
  if (!mission) return null;
  const raw = mission.ofpAttachment?.eteSec;
  const sec = raw == null || raw === "" ? NaN : Number(raw);
  if (Number.isFinite(sec) && sec > 0) return { min: sec / 60, label: "OFP" };
  const ete = finiteOrNull(mission.eteMin);
  if (ete != null && ete > 0) return { min: ete, label: "plan" };
  return null;
}

function elapsedOrigin(input: {
  ticket?: Pick<Mission, "actualDepartureUtc" | "actualDepartureSource"> | null;
  telemetry?: SimAtdTimes | null;
}): OpsElapsedFrom | null {
  const stamp = resolveTicketAtd({ ticket: input.ticket, telemetry: input.telemetry });
  if (!stamp) return null;
  if (parseAtdIso(input.ticket?.actualDepartureUtc)) return "atd";
  if (parseAtdIso(input.telemetry?.airborneUtc)) return "wheels-off";
  if (parseAtdIso(input.telemetry?.offBlocksUtc)) return "out";
  return "atd";
}

/**
 * Live flight clock for the ops strip.
 *
 * Elapsed = now − ATD (ticket, else wheels-off, else out). Never STD / taxi `startedAt`.
 * Remaining = planned OFP/ETE − elapsed, else the same live GS remaining as ETA. Empty if unknown.
 */
export function estimateFlightTime(input: {
  mission?: Mission | null;
  telemetry?: SimAtdTimes | null;
  eta?: Pick<EtaEstimate, "source" | "eteMin"> | null;
  now?: Date;
}): FlightTimeEstimate {
  const blank: FlightTimeEstimate = {
    elapsedMin: null,
    remainingMin: null,
    elapsedLabel: BLANK,
    remainingLabel: BLANK,
    source: "none",
    sourceLabel: "",
    elapsedFrom: null,
  };
  const mission = input.mission;
  if (!mission) return blank;

  const now = input.now ?? new Date();
  const atdIso = resolveTicketAtd({ ticket: mission, telemetry: input.telemetry })?.actualDepartureUtc ?? null;
  const start = coerceClockDate(atdIso);
  const done =
    mission.status === "completed" || mission.status === "cancelled"
      ? coerceClockDate(mission.completedAt)
      : null;
  const end = done && start && done.getTime() >= start.getTime() ? done : now;

  let elapsedMin: number | null = null;
  if (start && end.getTime() >= start.getTime()) {
    elapsedMin = minutesBetween(start, end);
  }
  const elapsedFrom = elapsedMin != null ? elapsedOrigin({ ticket: mission, telemetry: input.telemetry }) : null;

  const plan = plannedFlightMin(mission);
  if (plan && elapsedMin != null) {
    const remainingMin = Math.max(0, plan.min - elapsedMin);
    return {
      elapsedMin,
      remainingMin,
      elapsedLabel: formatOpsEte(elapsedMin),
      remainingLabel: formatOpsEte(remainingMin),
      source: "plan",
      sourceLabel: plan.label,
      elapsedFrom,
    };
  }

  if (input.eta?.source === "live" && input.eta.eteMin != null && input.eta.eteMin >= 0) {
    return {
      elapsedMin,
      remainingMin: input.eta.eteMin,
      elapsedLabel: formatOpsEte(elapsedMin),
      remainingLabel: formatOpsEte(input.eta.eteMin),
      source: "live",
      sourceLabel: "GS",
      elapsedFrom,
    };
  }

  if (elapsedMin != null) {
    return {
      elapsedMin,
      remainingMin: null,
      elapsedLabel: formatOpsEte(elapsedMin),
      remainingLabel: BLANK,
      source: "none",
      sourceLabel: "",
      elapsedFrom,
    };
  }

  return blank;
}

function assignedSpec(mission: Mission | undefined, fleet?: AircraftSpec[]): AircraftSpec | undefined {
  if (!mission) return undefined;
  return (fleet ?? []).find((a) => a.id === mission.aircraftId);
}

/** Short desk line for a strip row whose sim and ticket do not line up. */
export function groundingNoteFor(grounding: TicketGrounding | null): string {
  if (!grounding) return "";
  if (grounding.conflicts.length) return `Sim ≠ ticket — ${grounding.conflicts[0]}`;
  if (grounding.atOrigin === "mismatch" && grounding.atDest !== "match") {
    return `Sim ≠ ticket — aircraft at ${grounding.presence.atIcao ?? grounding.presence.nearestIcao}, ticket is ${grounding.originIcao}–${grounding.destIcao}`;
  }
  return "";
}

export function buildOpsStripRow(input: BuildOpsStripInput): OpsStripRow {
  const now = input.now ?? new Date();
  const crew = input.crew ?? null;
  const profile = input.profile ?? null;
  const bindOpts = {
    activeFlightId: input.activeFlightId,
    activeFlightAuthority: input.activeFlightAuthority,
    fused: input.fused,
    scenery: input.scenery,
    now,
  };
  const mission = input.mission ?? pickActiveMission(input.missions, bindOpts);
  const resolved =
    input.missions?.length
      ? resolveCurrentFlight({
          missions: input.missions,
          activeFlightId: input.activeFlightId,
          activeFlightAuthority: input.activeFlightAuthority,
          fused: input.fused,
          scenery: input.scenery,
          now,
        })
      : null;
  const bind =
    input.bindReason ??
    (mission && resolved?.mission?.id === mission.id ? resolved.reason : null);
  const spec = assignedSpec(mission, input.fleet);
  const ofp = mission?.ofpAttachment;
  const eta = estimateArrival({
    mission,
    fused: input.fused,
    scenery: input.scenery,
    now,
  });
  const phase = input.phase
    ? applyApproachOverride(input.phase, eta.remainingNm, input.fused ?? undefined)
    : deriveFlightPhase({
        fused: input.fused,
        now,
        simconnectEnabled: input.simconnectEnabled,
        simconnectError: input.simconnectError,
        destRemainingNm: eta.remainingNm,
      });
  const flightTime = estimateFlightTime({
    mission,
    telemetry: input.telemetry,
    eta,
    now,
  });

  const grounding =
    mission && input.fused
      ? groundTicket({
          mission,
          fused: input.fused,
          scenery: input.scenery,
          fleet: input.fleet,
          missions: input.missions,
          now,
        })
      : null;
  const crewId = trim(crew?.id) || trim(profile?.pilotNumber);
  const pilotName = trim(crew?.displayName) || trim(profile?.name) || BLANK;
  const kinematics = opsKinematicsFromFused(input.fused);
  const opsStatus = mission
    ? deriveFlightOpsStatus({
        ticket: {
          status: mission.status,
          departureIso: mission.departureIso,
          eteMin: mission.eteMin,
          startedAt: mission.startedAt,
          completedAt: mission.completedAt,
        },
        progress: {
          now,
          phase: phase.id,
          groundSpeedKt: kinematics.groundspeedKt,
          etaAtUtc: eta.atUtc,
          etaSource: eta.source,
          offBlocksUtc: input.telemetry?.offBlocksUtc,
          departedAtUtc: input.telemetry?.offBlocksUtc ?? mission.startedAt,
          arrivedAtUtc: mission.completedAt,
        },
      })
      : null;

  const flightNumber = mission ? trim(displayFlightNumber(mission)) || trim(mission.callsign) : "";
  const life = mission ? ticketLifecycle(mission) : null;
  const staMs = mission ? ticketStaMs(mission) : null;
  const staAt = staMs != null ? new Date(staMs) : null;
  const vsSta = compareEtaToSta(eta.atUtc, staMs);
  const divertLine = mission ? formatDiversionLine(mission) : "";

  return {
    id: crewId || "solo",
    pilotName,
    crewId,
    aircraftType: trim(spec?.icaoType) || trim(ofp?.aircraftType) || "",
    registration: trim(spec?.registration) || trim(mission?.registration) || trim(ofp?.registration) || "",
    callsign: trim(ofp?.callsign) || trim(input.callsign),
    flightNumber,
    missionId: mission?.id ?? "",
    lifecycle: life,
    lifecycleLabel: life ? TICKET_LIFECYCLE_LABEL[life] : "",
    lifecyclePillClass: life ? ticketStatusPillClass(life) : "",
    route: opsRouteLabel(mission),
    phase,
    etaLabel: formatOpsClockLabel(eta.atUtc, input.timeDisplay, input.timeZone),
    etaAtUtc: eta.atUtc,
    remainingNm: eta.remainingNm,
    totalNm: routeTotalNm(mission, input.scenery),
    etaSource: eta.source,
    eteMin: eta.eteMin,
    eteLabel: eta.eteLabel,
    staLabel: formatOpsClockLabel(staAt, input.timeDisplay, input.timeZone),
    staAtUtc: staAt ? staAt.toISOString() : null,
    etaVsSta: vsSta.etaVsSta,
    etaVsStaMin: vsSta.etaVsStaMin,
    etaVsStaLabel: vsSta.etaVsStaLabel,
    divertToIcao: trim(mission?.divertToIcao),
    divertReason: trim(mission?.divertReason),
    divertLine,
    bindReason: bind,
    bindLabel: bindLabelFor(bind),
    elapsedMin: flightTime.elapsedMin,
    elapsedLabel: flightTime.elapsedLabel,
    remainingMin: flightTime.remainingMin,
    remainingLabel: flightTime.remainingLabel,
    flightTimeSource: flightTime.source,
    flightTimeSourceLabel: flightTime.sourceLabel,
    elapsedFrom: flightTime.elapsedFrom,
    ...kinematics,
    opsStatus,
    opsStatusLabel: opsStatus ? FLIGHT_OPS_STATUS_LABEL[opsStatus] : "",
    grounding: grounding?.verdict ?? null,
    groundingNote: groundingNoteFor(grounding),
    networkNote: trim(input.networkNote),
    fuelLabel: trim(input.fuelLabel),
    fuelTitle: trim(input.fuelTitle),
  };
}

/**
 * Extra pilots only appear with an assigned ticket or a published fused
 * fix. Do not invent fused multi-pilot kinematics — a second row without
 * a usable lat/lon stays off the strip.
 */
function extraPilotHasWork(pilot: OpsStripPilotInput): boolean {
  if (pilot.mission) return true;
  const fused = pilot.fused;
  if (!fused || !Number.isFinite(fused.lat) || !Number.isFinite(fused.lon)) return false;
  if (Math.abs(fused.lat) > 90 || Math.abs(fused.lon) > 180) return false;
  return true;
}

/** STA/ETA paint. UTC instants stay on `*AtUtc`; Local uses the ops zone. */
export function formatOpsClockLabel(
  at: Date | string | null | undefined,
  timeDisplay?: GanttTimeDisplay,
  timeZone?: string,
): string {
  const date = coerceClockDate(at);
  if (!date) return BLANK;
  const mode = hydrateGanttTimeDisplay(timeDisplay ?? "zulu");
  if (mode === "zulu") return formatUtcHm(date);
  return formatHmInZone(date, ganttDisplayTimeZone("local", timeZone));
}

/**
 * Local signed-on crew is always row 0 (solo contract). Extra company pilots
 * emit another row only when they have an assigned trip or a published fix.
 */
export function buildOpsStripRows(input: BuildOpsStripInput): OpsStripRow[] {
  const local = buildOpsStripRow(input);
  const extras = (input.extraPilots ?? []).filter(extraPilotHasWork);
  if (!extras.length) return [local];
  const seen = new Set([local.id]);
  const rows = [local];
  for (const extra of extras) {
    const row = buildOpsStripRow({
      ...input,
      crew: extra.crew ?? null,
      profile: extra.profile ?? null,
      mission: extra.mission,
      fused: extra.fused ?? null,
      callsign: extra.callsign,
      phase: extra.phase,
      simconnectEnabled: extra.simconnectEnabled ?? false,
      simconnectError: extra.simconnectError,
      telemetry: extra.telemetry ?? null,
      networkNote: extra.networkNote ?? "",
      fuelLabel: "",
      fuelTitle: "",
      activeFlightId: extra.activeFlightId ?? input.activeFlightId,
      activeFlightAuthority: extra.activeFlightAuthority ?? input.activeFlightAuthority,
      bindReason: extra.bindReason ?? null,
    });
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    rows.push(row);
  }
  return rows;
}
